/**
 * The list behind "Без записей больше недели" (FIX-027 P4).
 *
 * A nested screen: opened from the home screen's memory block, back is
 * Telegram's own BackButton, so there is no header and no tab bar here (spec
 * §2/§6). Deliberately a plain Cell list rather than a filter bolted onto the
 * Projects screen — these tasks span projects, and the one thing worth showing
 * is how long each has been silent.
 *
 * Not a nag screen. It names what has gone quiet and links straight to the
 * journal; there is no "you should write more" copy anywhere in it.
 */

import { useQuery } from "@tanstack/react-query";
import { CircleDashed, NotebookPen } from "lucide-react";

import { Tasks } from "@/api/resources";
import { Cell, CellGroup } from "@/components/ui/Cell";
import { EmptyState, ErrorState } from "@/components/ui/EmptyState";
import { ScreenTitle } from "@/components/ui/Section";
import { SkeletonList } from "@/components/ui/Skeleton";
import { daysUntil, parseDate, pluralRu } from "@/lib/format";

/** Kept in step with DEFAULT_STALE_DAYS on the server. */
const STALE_DAYS = 7;

export function StaleTasksView() {
  const stale = useQuery({
    queryKey: ["tasks", "stale", STALE_DAYS],
    queryFn: () => Tasks.listStale(STALE_DAYS),
  });

  const rows = stale.data ?? [];

  return (
    <div className="space-y-4">
      <ScreenTitle eyebrow={`Тишина больше ${STALE_DAYS} дней`}>Без записей</ScreenTitle>

      {stale.isLoading && <SkeletonList />}

      {stale.isError && (
        <ErrorState
          title="Не удалось загрузить список"
          description="Проверь соединение и попробуй ещё раз."
          onRetry={() => void stale.refetch()}
          retrying={stale.isFetching}
        />
      )}

      {!stale.isLoading && !stale.isError && rows.length === 0 && (
        <EmptyState
          icon={<NotebookPen className="h-8 w-8" aria-hidden="true" />}
          title="Всё записано"
          description={`Ни одной активной задачи, о которой не писали больше ${STALE_DAYS} дней.`}
        />
      )}

      {rows.length > 0 && (
        <CellGroup inset="icon">
          {rows.map((task) => (
            <Cell
              key={task.id}
              icon={<CircleDashed className="h-5 w-5" aria-hidden="true" />}
              title={task.summary ?? task.title}
              subtitle={<SilentFor since={task.created_at} />}
              to={`/tasks/${task.id}`}
            />
          ))}
        </CellGroup>
      )}
    </div>
  );
}

/**
 * How long the task has existed, as the closest thing we have to "how long it
 * has been quiet" without a second request per row. The API orders oldest
 * first, so this reads as a descending scale down the list.
 */
function SilentFor({ since }: { since: string }) {
  const created = parseDate(since);
  if (!created) return null;
  const days = Math.abs(daysUntil(created));
  return (
    <>
      в работе {days} {pluralRu(days, "день", "дня", "дней")}
    </>
  );
}
